import { useEffect, useRef, useState } from 'react';
import { ensureExternalAccessFresh } from './api';
import { buildPeopleCountWebSocketUrl, parsePeopleCountMessage } from './peopleCounter';

export function usePeopleCountWebSocket(cameraId: string | null, enabled: boolean) {
  const [count, setCount] = useState<number | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled || !cameraId) {
      setCount(null);
      setConnected(false);
      setError(false);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const connect = async () => {
      try {
        await ensureExternalAccessFresh();
      } catch {
        if (!cancelled) setError(true);
        return;
      }

      const url = buildPeopleCountWebSocketUrl(cameraId);
      if (!url) {
        if (!cancelled) setError(true);
        return;
      }

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!cancelled) {
          setConnected(true);
          setError(false);
        }
      };

      ws.onmessage = (e) => {
        if (cancelled || typeof e.data !== 'string') return;
        const n = parsePeopleCountMessage(e.data);
        if (n !== null) setCount(n);
      };

      ws.onerror = () => {
        if (!cancelled) setError(true);
      };

      ws.onclose = () => {
        if (!cancelled) setConnected(false);
      };
    };

    void connect();

    return () => {
      cancelled = true;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [cameraId, enabled]);

  return { count, connected, error };
}
